import type { ItemDef, ItemId, LockKind, StoreDef, StoreId } from './types';

/**
 * THE CATALOGUE.
 *
 * Two numbers matter for every lootable object: dollars and kilograms. Their
 * ratio is the entire economy. Read the table as dollars-per-kilo:
 *
 *   loose diamonds   475,000 $/kg   the dream, behind a safe
 *   opioids            8,667 $/kg   pharmacy, behind a cabinet
 *   designer coat        643 $/kg   easy, unglamorous, reliable
 *   flat television      122 $/kg   a two-hand carry that ruins your round
 *   suit of armour       186 $/kg   a joke that is almost worth it
 *
 * Good loot is small, locked, and deep in the mall. Bad loot is heavy, open,
 * and by the door. That gradient is what makes tools worth their slot.
 */

const def = (d: ItemDef): ItemDef => d;

export const ITEMS: Record<ItemId, ItemDef> = Object.fromEntries(
  [
    // --- TOOLS ------------------------------------------------------------
    def({
      id: 'crowbar',
      name: 'Crowbar',
      kind: 'tool',
      value: 0,
      weight: 4.5,
      defeats: 'force',
      breachSec: 3.2,
      noise: 20,
      blurb: 'Opens shutters. Announces you.',
    }),
    def({
      id: 'lockpick',
      name: 'Pick Set',
      kind: 'tool',
      value: 0,
      weight: 0.3,
      defeats: 'pick',
      breachSec: 6.5,
      noise: 2,
      blurb: 'Slow and quiet. The professional option.',
    }),
    def({
      id: 'grinder',
      name: 'Angle Grinder',
      kind: 'tool',
      value: 0,
      weight: 6.2,
      defeats: 'cut',
      breachSec: 8.0,
      noise: 38,
      blurb: 'Cuts cages. Heard in the food court.',
    }),
    def({
      id: 'keycard',
      name: 'Cloned Staff Card',
      kind: 'tool',
      value: 0,
      weight: 0.02,
      defeats: 'card',
      breachSec: 1.2,
      noise: 0,
      blurb: 'STAFF ONLY. You are, briefly, staff.',
    }),
    def({
      id: 'drill',
      name: 'Core Drill',
      kind: 'tool',
      value: 0,
      weight: 9.4,
      defeats: 'safe',
      breachSec: 22,
      noise: 30,
      blurb: 'Twenty-two seconds you will remember.',
    }),

    // --- CONSUMABLES ------------------------------------------------------
    def({
      id: 'energyDrink',
      name: 'MAXIMUM Energy',
      kind: 'consumable',
      value: 0,
      weight: 0.4,
      effect: { kind: 'speed', magnitude: 1.22, durationSec: 18 },
      blurb: 'Tastes like a battery. Works.',
    }),
    def({
      id: 'painkillers',
      name: 'Industrial Painkillers',
      kind: 'consumable',
      value: 0,
      weight: 0.2,
      effect: { kind: 'strength', magnitude: 0.5, durationSec: 25 },
      blurb: 'Halves the weight. Not really.',
    }),
    def({
      id: 'jammer',
      name: 'Signal Jammer',
      kind: 'consumable',
      value: 0,
      weight: 1.1,
      effect: { kind: 'blind-cameras', magnitude: 18, durationSec: 20 },
      blurb: 'Cameras within 18m see static.',
    }),
    def({
      id: 'feltPads',
      name: 'Felt Pads',
      kind: 'consumable',
      value: 0,
      weight: 0.1,
      effect: { kind: 'silence', magnitude: 0.25, durationSec: 30 },
      blurb: 'For chair legs. And for you.',
    }),

    // --- JEWELLERY — the mascot store. Highest density in the mall. -------
    def({ id: 'diamonds', name: 'Loose Diamonds', kind: 'loot', value: 9500, weight: 0.02 }),
    def({ id: 'ring', name: 'Solitaire Ring', kind: 'loot', value: 4200, weight: 0.05 }),
    def({ id: 'watch', name: 'Chronograph', kind: 'loot', value: 3400, weight: 0.15 }),
    def({ id: 'goldChain', name: 'Gold Rope Chain', kind: 'loot', value: 1800, weight: 0.2 }),

    // --- PHARMACY ---------------------------------------------------------
    def({ id: 'opioids', name: 'Schedule II Cabinet', kind: 'loot', value: 2600, weight: 0.3 }),
    def({ id: 'insulin', name: 'Insulin Case', kind: 'loot', value: 400, weight: 0.2 }),
    def({ id: 'vitamins', name: 'Tub of Vitamins', kind: 'loot', value: 30, weight: 0.5 }),

    // --- ELECTRONICS ------------------------------------------------------
    def({ id: 'phone', name: 'Boxed Handset', kind: 'loot', value: 900, weight: 0.2 }),
    def({ id: 'laptop', name: 'Thin Laptop', kind: 'loot', value: 1400, weight: 2.2 }),
    def({ id: 'drone', name: 'Camera Drone', kind: 'loot', value: 700, weight: 1.5 }),
    def({ id: 'console', name: 'Games Console', kind: 'loot', value: 500, weight: 4.0 }),
    def({ id: 'television', name: 'Flat Television', kind: 'loot', value: 2200, weight: 18.0 }),

    // --- GUN STORE --------------------------------------------------------
    def({ id: 'pistol', name: 'Pistol', kind: 'loot', value: 700, weight: 1.1 }),
    def({ id: 'rifle', name: 'Sporting Rifle', kind: 'loot', value: 1600, weight: 4.2 }),
    def({ id: 'ammoCase', name: 'Case of Ammunition', kind: 'loot', value: 220, weight: 9.0 }),

    // --- MEDIEVAL WEAPONS — the shop that should not exist ----------------
    def({ id: 'longsword', name: 'Replica Longsword', kind: 'loot', value: 340, weight: 1.8 }),
    def({ id: 'halberd', name: 'Ceremonial Halberd', kind: 'loot', value: 900, weight: 4.5 }),
    def({
      id: 'armour',
      name: 'Full Suit of Armour',
      kind: 'loot',
      value: 5200,
      weight: 28.0,
      blurb: 'You will not be running.',
    }),

    // --- CLOTHING / COSMETICS / BOOKS ------------------------------------
    def({ id: 'coat', name: 'Designer Coat', kind: 'loot', value: 900, weight: 1.4 }),
    def({ id: 'sneakers', name: 'Boxed Sneakers', kind: 'loot', value: 260, weight: 0.9 }),
    def({ id: 'perfume', name: 'Perfume Set', kind: 'loot', value: 180, weight: 0.3 }),
    def({ id: 'firstEdition', name: 'First Edition', kind: 'loot', value: 2400, weight: 1.1 }),

    // --- SUPERMARKET / DEPARTMENT / FOOD COURT ---------------------------
    def({ id: 'till', name: 'Cash Drawer', kind: 'loot', value: 380, weight: 1.2 }),
    def({ id: 'safeCash', name: 'Banded Cash', kind: 'loot', value: 7800, weight: 1.6 }),
    def({ id: 'steak', name: 'Prime Cut', kind: 'loot', value: 70, weight: 1.5 }),
    def({ id: 'espressoMachine', name: 'Espresso Machine', kind: 'loot', value: 600, weight: 12.0 }),
    def({ id: 'vacuum', name: 'Upright Vacuum', kind: 'loot', value: 210, weight: 6.0 }),
    def({ id: 'skateDeck', name: 'Pro Deck', kind: 'loot', value: 180, weight: 2.0 }),
  ].map((d) => [d.id, d]),
);

/**
 * THE MALL DIRECTORY.
 *
 * Lock kind is the difficulty gate. An unlocked store is a tutorial; a `safe`
 * store is a heist inside the heist. Note that the two richest stores are also
 * the two that need a tool you had to choose over another tool.
 */
export const STORES: Record<StoreId, StoreDef> = Object.fromEntries(
  (
    [
      {
        id: 'jewellery',
        name: 'ETERNAL YOU',
        tagline: 'FOREVER, PRICED ACCORDINGLY',
        lock: 'cut',
        spawns: [
          { item: 'ring', count: 3 },
          { item: 'watch', count: 3 },
          { item: 'goldChain', count: 4 },
          { item: 'diamonds', count: 1 },
        ],
      },
      {
        id: 'pharmacy',
        name: 'LIFE SUPPLIES',
        tagline: 'ASK OUR PHARMACIST ANYTHING',
        lock: 'pick',
        spawns: [
          { item: 'opioids', count: 2 },
          { item: 'insulin', count: 4 },
          { item: 'vitamins', count: 6 },
          { item: 'painkillers', count: 3 },
        ],
      },
      {
        id: 'electronics',
        name: 'CIRCUIT PLANET',
        tagline: 'THE FUTURE, IN STOCK',
        lock: 'force',
        spawns: [
          { item: 'phone', count: 5 },
          { item: 'laptop', count: 3 },
          { item: 'drone', count: 2 },
          { item: 'console', count: 3 },
          { item: 'television', count: 2 },
          { item: 'jammer', count: 2 },
        ],
      },
      {
        id: 'guns',
        name: 'LIBERTY OUTFITTERS',
        tagline: 'SPORTING GOODS AND ACCESSORIES',
        lock: 'cut',
        spawns: [
          { item: 'pistol', count: 3 },
          { item: 'rifle', count: 2 },
          { item: 'ammoCase', count: 2 },
        ],
      },
      {
        id: 'medieval',
        name: 'MEDIEVAL WEAPONS',
        tagline: 'AUTHENTIC REPRODUCTIONS SINCE 1994',
        lock: 'force',
        spawns: [
          { item: 'longsword', count: 4 },
          { item: 'halberd', count: 2 },
          { item: 'armour', count: 1 },
        ],
      },
      {
        id: 'clothing',
        name: 'ORBITAL OUTFITTERS',
        tagline: 'DRESS FOR THE LIFE YOU WANT',
        lock: 'none',
        spawns: [
          { item: 'coat', count: 4 },
          { item: 'sneakers', count: 5 },
        ],
      },
      {
        id: 'department',
        name: 'DEPARTMENT OF THINGS',
        tagline: 'EVERYTHING YOU MIGHT NEED',
        /**
         * The only badge door on the shop floor, and the reason the keycard is on
         * the bench at all. Without it the cheapest tool in the game opens
         * nothing, which makes one of five slots on the loadout table a trap.
         *
         * It earns its place by being the quiet option: twenty grams, 1.2 seconds
         * and no noise at all clears a seven-thousand-dollar store, against a
         * grinder that costs six kilos, eight seconds and the loudest sound in the
         * building. Same door either way. The keycard is what you bring when you
         * would rather nobody knew.
         */
        lock: 'card',
        spawns: [
          { item: 'till', count: 3 },
          { item: 'vacuum', count: 3 },
          { item: 'perfume', count: 4 },
          { item: 'firstEdition', count: 2 },
        ],
      },
      {
        id: 'supermarket',
        name: 'NOURISH YOUR NEEDS',
        tagline: 'OPEN 24 HOURS (CLOSED)',
        lock: 'none',
        spawns: [
          { item: 'till', count: 4 },
          { item: 'steak', count: 6 },
          { item: 'energyDrink', count: 6 },
          { item: 'safeCash', count: 1 },
        ],
      },
      {
        id: 'foodcourt',
        name: 'THE EATING PLACE',
        tagline: 'PLEASE RETURN YOUR TRAY',
        lock: 'none',
        spawns: [
          { item: 'espressoMachine', count: 3 },
          { item: 'till', count: 2 },
          { item: 'feltPads', count: 2 },
        ],
      },
      {
        id: 'skate',
        name: 'CONCRETE WAVE',
        tagline: 'NO SKATING IN THE MALL',
        lock: 'none',
        spawns: [
          { item: 'skateDeck', count: 6 },
          { item: 'sneakers', count: 3 },
          { item: 'crowbar', count: 1 },
        ],
      },
    ] satisfies StoreDef[]
  ).map((s) => [s.id, s]),
);

/** Total dollar value on the floor at spawn. Used to sanity-check the economy. */
export function totalMallValue(): number {
  let sum = 0;
  for (const store of Object.values(STORES)) {
    for (const s of store.spawns) sum += (ITEMS[s.item]?.value ?? 0) * s.count;
  }
  return sum;
}

/** The tool, if any, that defeats a given lock. */
export function toolFor(lock: LockKind): ItemDef | null {
  if (lock === 'none') return null;
  for (const item of Object.values(ITEMS)) {
    if (item.kind === 'tool' && item.defeats === lock) return item;
  }
  return null;
}

export function item(id: ItemId): ItemDef {
  const found = ITEMS[id];
  if (!found) throw new Error(`unknown item: ${id}`);
  return found;
}
